import React, { useMemo, useState, useEffect } from 'react';
import { Select, MenuItem, Skeleton } from '@mui/material';
import { minimalInputTheme } from '@/app/theme';
import { useGetIndustriesQuery } from '@/features/properties/propertiesApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

export const IndustrySelect = ({ initialValue, disabled, onChange, minimal }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const industryData = useGetIndustriesQuery(undefined, { skip: !userDetails?.userId });
	const nullEntry = { value: null, label: 'None' };

	const industries = useMemo(() => {
		if (!industryData?.data) return [nullEntry];

		const mappedIndustries = industryData?.data?.Results?.map((item) => ({
			value: item.Value,
			label: item.Label,
		}));

		return [nullEntry, ...(mappedIndustries ?? [])];
	}, [industryData?.data]);

	const [industry, setIndustry] = useState(() => {
		return (
			industries.find((o) => o.value === initialValue || o.label === initialValue) ??
			nullEntry
		);
	});

	useEffect(() => {
		const validIndustry = industries.find(
			(o) => o.value === industry.value || o.label === industry.label,
		);
		if (!validIndustry) setIndustry(nullEntry);
	}, [industries]);

	useEffect(() => {
		const selected = industries.find((o) => o.value == initialValue || o.label == initialValue);
		setIndustry(selected ?? nullEntry);
	}, [initialValue, industries]);

	const updateIndustry = (industryValue) => {
		const selected = industries.find(
			(option) => option.value === industryValue || option.label === industryValue,
		);
		const newIndustry = selected ?? nullEntry;
		setIndustry(newIndustry);
		if (newIndustry.value !== '') {
			onChange && onChange(newIndustry.value);
		}
	};


	if (industryData?.isLoading || industryData?.isUninitialized) {
		return <Skeleton width='100%' height={40} />;
	}

	return (
		<Select
			className={minimal && 'minimal-input'}
			value={industry.value ?? ''}
			size={minimal ? 'small' : 'medium'}
			disabled={disabled || industryData?.isLoading}
			onChange={(e) => updateIndustry(e.target.value)}
			displayEmpty
			fullWidth
			variant={minimal ? 'standard' : 'outlined'}
			sx={{
				...(minimal && {
					...minimalInputTheme,
					'& .Mui-disabled': {
						backgroundColor: 'transparent',
					},
				}),
			}}
		>
			{industries.map((option) => (
				<MenuItem value={option.value} key={`client-industry-${option.value}`}>
					{option.label}
				</MenuItem>
			))}
		</Select>
	);
};
